import {
  PortalDsBadge,
  PortalDsEmptyState,
  portalDs,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  adaptSurveyListRows,
  formatSurveyStatusLabel,
  isEditableSurveyStatus,
} from 'src/redux/survey/surveys-api-v1/surveys-api-v1.adapters';
import type { SurveysApiSurveySummary } from 'src/redux/survey/surveys-api-v1/surveys-api-v1.types';
import { surveyStatusPillVariant } from '../utils/survey-status-pill';

function formatDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface SurveysBetaTableProps {
  items: SurveysApiSurveySummary[];
  selectedIds: Set<string>;
  onToggleSelect: (extSurveyId: string) => void;
  onToggleSelectAll: () => void;
  onRowClick: (extSurveyId: string, status: string) => void;
  isLoading?: boolean;
  isError?: boolean;
}

export function SurveysBetaTable({
  items,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onRowClick,
  isLoading,
  isError,
}: SurveysBetaTableProps) {
  const rows = adaptSurveyListRows(items);
  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.extSurveyId));

  if (isError) {
    return (
      <PortalDsEmptyState
        title="Could not load surveys"
        description="The Surveys API returned an error. Check entitlements and try again."
      />
    );
  }

  if (!isLoading && rows.length === 0) {
    return (
      <PortalDsEmptyState
        title="No surveys yet"
        description="Create a survey from scratch to get started with the Surveys API."
      />
    );
  }

  return (
    <div className={portalDs.tableContainer}>
      <table className="w-full min-w-[720px] border-collapse text-left">
        <thead className={portalDs.tableHead}>
          <tr>
            <th className={cn(portalDs.tableHeadCell, 'w-10')}>
              <input
                type="checkbox"
                className={portalDs.formCheckInput}
                checked={allSelected}
                onChange={onToggleSelectAll}
                aria-label="Select all surveys"
              />
            </th>
            <th className={portalDs.tableHeadCell}>
              Title
            </th>
            <th className={portalDs.tableHeadCell}>
              Status
            </th>
            <th className={portalDs.tableHeadCell}>
              Type
            </th>
            <th className={portalDs.tableHeadCell}>
              Brand
            </th>
            <th className={portalDs.tableHeadCell}>
              Start
            </th>
            <th className={portalDs.tableHeadCell}>
              End
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading && rows.length === 0 ? (
            <tr>
              <td
                className={portalDs.tableCell}
                colSpan={7}
              >
                <span className={portalDs.bodySm}>
                  Loading surveys…
                </span>
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const selected = selectedIds.has(row.extSurveyId);
              return (
                <tr
                  key={row.extSurveyId}
                  className={cn(
                    'group',
                    portalDs.tableBodyRow,
                    portalDs.tableBodyRowInteractive,
                    selected && portalDs.tableBodyRowSelected
                  )}
                  onClick={() => onRowClick(row.extSurveyId, row.surveyStatus)}
                >
                  <td
                    className={portalDs.tableCell}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      className={portalDs.formCheckInput}
                      checked={selected}
                      onChange={() => onToggleSelect(row.extSurveyId)}
                      aria-label={`Select ${row.title}`}
                    />
                  </td>
                  <td className={portalDs.tableCell}>
                    <div className="font-semibold text-[#0D0F1A]">
                      {row.title}
                    </div>
                    {isEditableSurveyStatus(row.surveyStatus) ? (
                      <div className={portalDs.caption}>
                        Draft — open to edit
                      </div>
                    ) : null}
                  </td>
                  <td className={portalDs.tableCell}>
                    <PortalDsBadge
                      pill
                      variant={surveyStatusPillVariant(row.surveyStatus)}
                    >
                      {formatSurveyStatusLabel(row.surveyStatus)}
                    </PortalDsBadge>
                  </td>
                  <td className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                    {row.surveyType || '—'}
                  </td>
                  <td className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                    {row.brandName || '—'}
                  </td>
                  <td className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                    {formatDate(row.startDate)}
                  </td>
                  <td className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                    {formatDate(row.endDate)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
